import { useEffect, useRef, useState } from 'react'
import {
  Alert, Button, Code, FileButton, Group, Loader, Paper, Stack, Text, TextInput, Textarea,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconCertificate, IconDownload, IconInfoCircle, IconUpload } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import {
  ESTADOS_DA_INICIALIZACAO, comandoDeAssinatura, erroDaGestao, gravarAncoraDoCluster,
  inicializarCluster, lerCsrDoCluster,
  type ClusterCloudHsm,
} from '@/api/cloudhsm'

/**
 * Inicialização do cluster pela tela (F3, consoleai#26221; spec 2026-11-05 §3).
 *
 * Três passos: baixar o CSR, assinar FORA do ConsoleAI com a CA do cliente (o comando
 * `openssl x509 -req` vem pronto) e colar ou enviar os dois PEM públicos — o certificado do HSM
 * e a âncora (customerCA.crt). A chave da CA nunca passa por aqui. O backend confere os dois
 * antes de chamar a AWS, grava a âncora no SSM (o executor a lê para ativar) e inicializa.
 *
 * Depois de enviar, o cartão relê o estado até a AWS sair de INITIALIZE_IN_PROGRESS; com o
 * cluster em INITIALIZED, quem monta isto leva para a aba Usuários, onde está "Ativar cluster".
 *
 * Cluster já inicializado à mão (sem a âncora no SSM, a ativação falha): "Gravar âncora".
 */

export const INTERVALO_DA_INICIALIZACAO_MS = 5_000
/** A AWS leva alguns minutos para inicializar; depois disso o botão Atualizar resolve. */
export const JANELA_DA_INICIALIZACAO_MS = 15 * 60_000

const ehPem = (v: string) => v.includes('-----BEGIN CERTIFICATE-----') && v.includes('-----END CERTIFICATE-----')

interface Props {
  tenantId: number
  cluster: ClusterCloudHsm
  podeGerir: boolean
  onAtualizar: () => void
  /** Chamado quando a AWS aceitou a inicialização. */
  onInicializado: () => void
}

/** O que o cartão do cluster mostra conforme o estado: inicializar, ou gravar a âncora. */
export function InicializacaoDoCluster(props: Props) {
  if (ESTADOS_DA_INICIALIZACAO.includes(props.cluster.estado)) return <InicializarCluster {...props} />
  if (props.cluster.estado === 'INITIALIZED' && props.podeGerir) {
    return <GravarAncora tenantId={props.tenantId} cluster={props.cluster} />
  }
  return null
}

function baixar(nome: string, conteudo: string) {
  if (typeof URL.createObjectURL !== 'function') return
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'application/pkcs10' }))
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  a.click()
  URL.revokeObjectURL(url)
}

function CampoPem({ rotulo, descricao, valor, onChange }: {
  rotulo: string
  descricao: string
  valor: string
  onChange: (v: string) => void
}) {
  return (
    <Stack gap={4}>
      <Textarea label={rotulo} description={descricao} value={valor} autosize minRows={3} maxRows={8}
        styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
        placeholder="-----BEGIN CERTIFICATE-----"
        error={valor && !ehPem(valor) ? 'Não parece um certificado em PEM.' : null}
        onChange={(e) => onChange(e.currentTarget.value)} />
      <Group>
        <FileButton accept=".crt,.pem,.cer,text/plain" onChange={(f) => { if (f) void f.text().then(onChange) }}>
          {(p) => <Button {...p} size="xs" variant="subtle" leftSection={<IconUpload size={14} />}>Enviar arquivo</Button>}
        </FileButton>
      </Group>
    </Stack>
  )
}

export function InicializarCluster({ tenantId, cluster, podeGerir, onAtualizar, onInicializado }: Props) {
  const id = cluster.cluster_id
  const [csr, setCsr] = useState<string | null>(null)
  const [certificado, setCertificado] = useState('')
  const [ancora, setAncora] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviadoEm, setEnviadoEm] = useState<number | null>(null)
  const emAndamento = cluster.estado === 'INITIALIZE_IN_PROGRESS'

  // Relê o estado enquanto a AWS inicializa (ou até a janela acabar). O `onAtualizar` vai por ref:
  // quem monta passa uma função nova a cada render, e o intervalo recomeçaria a cada leitura.
  const atualizar = useRef(onAtualizar)
  atualizar.current = onAtualizar
  const releia = enviadoEm !== null || emAndamento
  useEffect(() => {
    if (!releia) return
    const inicio = enviadoEm ?? Date.now()
    const t = setInterval(() => {
      if (Date.now() - inicio > JANELA_DA_INICIALIZACAO_MS) { clearInterval(t); return }
      atualizar.current()
    }, INTERVALO_DA_INICIALIZACAO_MS)
    return () => clearInterval(t)
  }, [releia, enviadoEm])

  const lerCsr = useMutation({
    mutationFn: () => lerCsrDoCluster(tenantId, id, cluster.regiao),
    onMutate: () => setErro(null),
    onSuccess: (d) => { setCsr(d.csr); baixar(`${id}_ClusterCsr.csr`, d.csr) },
    onError: (e) => setErro(erroDaGestao(e, 'Não foi possível ler o CSR do cluster.')),
  })
  const inicializar = useMutation({
    mutationFn: () => inicializarCluster(tenantId, id, cluster.regiao, {
      certificado: certificado.trim(), ancora: ancora.trim(), confirmacao,
    }),
    onMutate: () => setErro(null),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Inicialização enviada. Falta ativar o cluster.' })
      setEnviadoEm(Date.now())
      onInicializado()
      onAtualizar()
    },
    onError: (e) => setErro(erroDaGestao(e, 'Não foi possível inicializar o cluster.')),
  })

  if (emAndamento || enviadoEm !== null) {
    return (
      <Alert color="blue" icon={<Loader size={16} />} title="Inicialização em andamento">
        A AWS está inicializando o cluster; isto leva alguns minutos. Quando terminar, a aba Usuários
        mostra <b>Ativar cluster</b>.
      </Alert>
    )
  }

  if (!podeGerir) {
    return (
      <Alert color="yellow" icon={<IconInfoCircle size={16} />} title="Cluster ainda não inicializado">
        Inicializar o cluster exige a permissão cloudhsm:manage neste tenant.
      </Alert>
    )
  }

  const pronto = ehPem(certificado) && ehPem(ancora) && confirmacao === id

  return (
    <Paper withBorder p="md" radius="sm">
      <Stack gap="md">
        <Group gap="xs">
          <IconCertificate size={18} />
          <Text fw={700}>Inicializar o cluster</Text>
        </Group>
        <Text size="sm" c="dimmed">
          O cluster está em UNINITIALIZED. A inicialização é irreversível: a âncora (a CA do cliente)
          não troca sem recriar o cluster. A chave da CA fica com o cliente; o ConsoleAI só recebe
          certificados públicos.
        </Text>

        <Stack gap={4}>
          <Text size="sm" fw={600}>1. Baixe o CSR do cluster</Text>
          <Group>
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
              loading={lerCsr.isPending} onClick={() => lerCsr.mutate()}>
              Baixar CSR
            </Button>
          </Group>
          {csr && <Code block aria-label="CSR do cluster">{csr}</Code>}
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={600}>2. Assine o CSR com a CA do cliente, fora do ConsoleAI</Text>
          <Code block aria-label="Comando de assinatura">{comandoDeAssinatura(id)}</Code>
        </Stack>

        <Stack gap="xs">
          <Text size="sm" fw={600}>3. Cole ou envie os dois certificados</Text>
          <CampoPem rotulo="Certificado do HSM assinado" valor={certificado} onChange={setCertificado}
            descricao={`${id}_CustomerHsmCertificate.crt, o que o comando gerou`} />
          <CampoPem rotulo="Âncora (certificado da CA)" valor={ancora} onChange={setAncora}
            descricao="customerCA.crt, autoassinado; fica no SSM para a ativação" />
          <TextInput label="Para confirmar, digite o id do cluster"
            description={<>Exatamente como está: <Code>{id}</Code></>} value={confirmacao}
            error={confirmacao && confirmacao !== id ? 'Não confere.' : null} autoComplete="off"
            onChange={(e) => setConfirmacao(e.currentTarget.value)} />
        </Stack>

        {erro && <Alert color="red" icon={<IconAlertTriangle size={16} />}>{erro}</Alert>}

        <Group justify="flex-end">
          <Button color="orange" disabled={!pronto} loading={inicializar.isPending}
            onClick={() => inicializar.mutate()}>
            Inicializar cluster
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

/** Cluster inicializado fora do ConsoleAI: grava a âncora no SSM para a ativação ler. */
export function GravarAncora({ tenantId, cluster }: { tenantId: number; cluster: ClusterCloudHsm }) {
  const [aberto, setAberto] = useState(false)
  const [ancora, setAncora] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const gravar = useMutation({
    mutationFn: () => gravarAncoraDoCluster(tenantId, cluster.cluster_id, cluster.regiao, ancora.trim()),
    onMutate: () => setErro(null),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Âncora gravada no SSM. A ativação já pode lê-la.' })
      setAberto(false)
    },
    onError: (e) => setErro(erroDaGestao(e, 'Não foi possível gravar a âncora.')),
  })
  useEffect(() => { setAncora(''); setErro(null) }, [aberto])

  if (!aberto) {
    return (
      <Group gap="xs">
        <Text size="sm" c="dimmed">Inicializado fora do ConsoleAI e a ativação não acha a âncora?</Text>
        <Button size="xs" variant="subtle" onClick={() => setAberto(true)}>Gravar âncora</Button>
      </Group>
    )
  }

  return (
    <Paper withBorder p="md" radius="sm">
      <Stack gap="sm">
        <Text fw={700}>Âncora de {cluster.cluster_id}</Text>
        <Text size="sm">
          A âncora tem de ser a CA que assinou o certificado deste cluster: o ConsoleAI confere com a
          AWS antes de gravar.
        </Text>
        <CampoPem rotulo="Âncora (certificado da CA)" valor={ancora} onChange={setAncora}
          descricao="customerCA.crt, autoassinado" />
        {erro && <Alert color="red" icon={<IconAlertTriangle size={16} />}>{erro}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setAberto(false)}>Cancelar</Button>
          <Button disabled={!ehPem(ancora)} loading={gravar.isPending} onClick={() => gravar.mutate()}>
            Gravar
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}
